import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  TrendingUp, Calendar, Download, DollarSign,
  CreditCard, Banknote, RefreshCcw, Smartphone, Globe, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPaymentsPage = () => {
  const { token } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [report, setReport] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Default date range is current date
  const today = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; })();
  const [dateRange, setDateRange] = useState({
    start_date: today,
    end_date: today
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(dateRange);
      const [reportRes, refundsRes, bookingsRes] = await Promise.all([
        fetch(`${API}/admin/payments/report?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/admin/payments/refunds`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/admin/bookings?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}&limit=1000`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (reportRes.ok) setReport(await reportRes.json());
      if (refundsRes.ok) setRefunds(await refundsRes.json());
      if (bookingsRes.ok) {
        const data = await bookingsRes.json();
        setBookings(data.bookings || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  // For detailed report - filter paid bookings from bookings list
  const paidBookings = bookings.filter(b => b.payment_status === 'paid' && b.status !== 'cancelled');
  
  // Group special package bookings by booked_at + customer_name (for detailed report only)
  const getUniqueBookings = () => {
    const regularBookings = paidBookings.filter(b => !b.is_special_package);
    const specialPackageBookings = paidBookings.filter(b => b.is_special_package);
    
    // Group special packages by booked_at + customer_name
    const specialPackageGroups = {};
    specialPackageBookings.forEach(booking => {
      const key = `${booking.booked_at}_${booking.customer_name}`;
      if (!specialPackageGroups[key]) {
        specialPackageGroups[key] = {
          ...booking,
          bookings: [booking]
        };
      } else {
        // Accumulate total_amount if split across multiple bookings
        specialPackageGroups[key].total_amount = (specialPackageGroups[key].total_amount || 0) + (booking.total_amount || 0);
        specialPackageGroups[key].bookings.push(booking);
      }
    });
    
    // Return consolidated list - each special package group counts as 1 booking
    return {
      regularBookings,
      specialPackageGroups: Object.values(specialPackageGroups),
      uniqueCount: regularBookings.length + Object.keys(specialPackageGroups).length
    };
  };
  
  const { regularBookings, specialPackageGroups } = getUniqueBookings();
  
  // Get revenue stats from report API (filtered by created_at date)
  const totalRevenue = report?.total_revenue || 0;
  const totalBookingsCount = report?.total_bookings || 0;
  const avgPerBooking = totalBookingsCount > 0 ? totalRevenue / totalBookingsCount : 0;

  // Payment method breakdown from report data
  const paymentBreakdown = {
    cash: report?.payment_methods?.cash || 0,
    card: report?.payment_methods?.card || 0,
    ewallet: report?.payment_methods?.ewallet || report?.payment_methods?.['e-wallet'] || 0,
    online: report?.payment_methods?.online || report?.payment_methods?.online_banking || 0
  };

  // Daily revenue from report data (by created_at date)
  const dailyRevenue = report?.daily_revenue || {};

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  // Download Summary Report (Excel)
  const downloadSummaryReport = () => {
    try {
      // Create CSV content for summary
      let csv = 'Payment Report Summary\n';
      csv += `Date Range: ${dateRange.start_date} to ${dateRange.end_date}\n\n`;
      csv += 'Category,Amount\n';
      csv += `Total Revenue,${totalRevenue}\n`;
      csv += `Total Paid Bookings,${totalBookingsCount}\n`;
      csv += `Average per Booking,${avgPerBooking.toFixed(2)}\n\n`;
      csv += 'Payment Method Breakdown\n';
      csv += 'Payment Method,Amount\n';
      csv += `Cash,${paymentBreakdown.cash}\n`;
      csv += `Card,${paymentBreakdown.card}\n`;
      csv += `E-Wallet,${paymentBreakdown.ewallet}\n`;
      csv += `Online Banking,${paymentBreakdown.online}\n\n`;
      csv += 'Daily Revenue\n';
      csv += 'Date,Amount\n';
      Object.entries(dailyRevenue)
        .sort(([a], [b]) => b.localeCompare(a))
        .forEach(([date, amount]) => {
          csv += `${date},${amount}\n`;
        });

      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `payment_summary_${dateRange.start_date}_to_${dateRange.end_date}.csv`;
      link.click();
      toast.success('Summary report downloaded');
    } catch (error) {
      toast.error('Failed to download summary');
    }
  };

  // Download Detailed Report (Excel)
  const downloadDetailedReport = () => {
    try {
      // Create CSV content for detailed report
      let csv = 'Detailed Payment Report\n';
      csv += `Date Range: ${dateRange.start_date} to ${dateRange.end_date}\n\n`;
      csv += 'Customer Name,Email,Phone,Booking Date(s),Court(s),Time,Transaction Type,Payment Method,Court Rental Amount,Add-ons Amount,Total Amount\n';
      
      // Process regular bookings
      regularBookings.forEach(booking => {
        const customerName = (booking.customer_name || booking.user?.name || 'Guest').replace(/,/g, ' ');
        const email = (booking.customer_email || booking.user?.email || 'N/A').replace(/,/g, ' ');
        const phone = (booking.customer_phone || booking.user?.phone || 'N/A').replace(/,/g, ' ');
        const bookingDate = booking.date || 'N/A';
        const court = booking.court_id || 'N/A';
        const time = `${booking.start_time || ''} - ${booking.end_time || ''}`;
        const transactionType = booking.booking_type || 'Standard';
        const paymentMethod = booking.payment_method || 'N/A';
        
        const addOnsData = booking.add_ons?.length > 0 ? booking.add_ons : booking.equipment_rentals || [];
        const addOnsAmount = addOnsData.reduce((sum, addon) => sum + ((addon.price || 0) * (addon.quantity || 1)), 0);
        const courtRentalAmount = (booking.total_amount || 0) - addOnsAmount;
        
        csv += `"${customerName}","${email}","${phone}","${bookingDate}","${court}","${time}","${transactionType}","${paymentMethod}",${courtRentalAmount},${addOnsAmount},${booking.total_amount || 0}\n`;
      });
      
      // Process special package bookings (grouped)
      specialPackageGroups.forEach(group => {
        const customerName = (group.customer_name || group.user?.name || 'Guest').replace(/,/g, ' ');
        const email = (group.customer_email || group.user?.email || 'N/A').replace(/,/g, ' ');
        const phone = (group.customer_phone || group.user?.phone || 'N/A').replace(/,/g, ' ');
        
        // Combine all dates and courts from the package
        const dates = group.bookings.map(b => b.date).sort().join('; ');
        const courts = [...new Set(group.bookings.map(b => b.court_id))].join('; ');
        const times = group.bookings.map(b => `${b.start_time}-${b.end_time}`).join('; ');
        
        const transactionType = `Special Package: ${group.special_package_name || 'Package'}`;
        const paymentMethod = group.payment_method || 'N/A';
        
        // Add-ons from first booking (where they're typically stored)
        const addOnsData = group.add_ons?.length > 0 ? group.add_ons : group.equipment_rentals || [];
        const addOnsAmount = addOnsData.reduce((sum, addon) => sum + ((addon.price || 0) * (addon.quantity || 1)), 0);
        const courtRentalAmount = (group.total_amount || 0) - addOnsAmount;
        
        csv += `"${customerName}","${email}","${phone}","${dates}","${courts}","${times}","${transactionType}","${paymentMethod}",${courtRentalAmount},${addOnsAmount},${group.total_amount || 0}\n`;
      });

      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `detailed_payment_report_${dateRange.start_date}_to_${dateRange.end_date}.csv`;
      link.click();
      toast.success('Detailed report downloaded');
    } catch (error) {
      toast.error('Failed to download detailed report');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${theme.heading}`}>Payments Report</h1>
            <p className={`${theme.textMuted} mt-1`}>Revenue and payment analytics</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex gap-2">
              <Input
                type="date"
                value={dateRange.start_date}
                onChange={(e) => setDateRange(prev => ({ ...prev, start_date: e.target.value }))}
                className={theme.input}
              />
              <Input
                type="date"
                value={dateRange.end_date}
                onChange={(e) => setDateRange(prev => ({ ...prev, end_date: e.target.value }))}
                className={theme.input}
              />
            </div>
          </div>
        </div>

        {/* Download Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={downloadSummaryReport}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={loading || paidBookings.length === 0}
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Download Summary
          </Button>
          <Button 
            onClick={downloadDetailedReport}
            variant="outline"
            className={`${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            disabled={loading || paidBookings.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Detailed Report
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Summary Cards - Row 1 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className={theme.statCard}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm ${theme.statLabel}`}>Total Revenue (Paid)</p>
                      <p className="text-2xl font-bold text-emerald-500 mt-1">
                        {formatCurrency(totalRevenue)}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-emerald-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={theme.statCard}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm ${theme.statLabel}`}>Total Bookings (Paid)</p>
                      <p className="text-2xl font-bold text-blue-500 mt-1">
                        {totalBookingsCount}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-blue-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={theme.statCard}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm ${theme.statLabel}`}>Avg per Booking</p>
                      <p className="text-2xl font-bold text-amber-500 mt-1">
                        {formatCurrency(avgPerBooking)}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Payment Method Breakdown Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className={theme.statCard}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <Banknote className="h-5 w-5 text-green-500" />
                    </div>
                    <div>
                      <p className={`text-xs ${theme.statLabel}`}>Cash</p>
                      <p className={`text-lg font-bold ${theme.statValue}`}>
                        {formatCurrency(paymentBreakdown.cash)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={theme.statCard}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <CreditCard className="h-5 w-5 text-purple-500" />
                    </div>
                    <div>
                      <p className={`text-xs ${theme.statLabel}`}>Card</p>
                      <p className={`text-lg font-bold ${theme.statValue}`}>
                        {formatCurrency(paymentBreakdown.card)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={theme.statCard}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <Smartphone className="h-5 w-5 text-cyan-500" />
                    </div>
                    <div>
                      <p className={`text-xs ${theme.statLabel}`}>E-Wallet</p>
                      <p className={`text-lg font-bold ${theme.statValue}`}>
                        {formatCurrency(paymentBreakdown.ewallet)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={theme.statCard}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                      <Globe className="h-5 w-5 text-indigo-500" />
                    </div>
                    <div>
                      <p className={`text-xs ${theme.statLabel}`}>Online</p>
                      <p className={`text-lg font-bold ${theme.statValue}`}>
                        {formatCurrency(paymentBreakdown.online)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Daily Revenue */}
            <Card className={theme.card}>
              <CardHeader>
                <CardTitle className={theme.heading}>Daily Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(dailyRevenue).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(dailyRevenue)
                      .sort(([a], [b]) => b.localeCompare(a))
                      .slice(0, 14)
                      .map(([date, amount]) => (
                        <div key={date} className={`flex items-center justify-between p-3 rounded ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-100'}`}>
                          <span className={theme.textMuted}>{date}</span>
                          <span className="text-emerald-500 font-medium">{formatCurrency(amount)}</span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className={`${theme.textMuted} text-center py-8`}>No revenue data for this period</p>
                )}
              </CardContent>
            </Card>

            {/* Refunds */}
            <Card className={theme.card}>
              <CardHeader>
                <CardTitle className={`${theme.heading} flex items-center gap-2`}>
                  <RefreshCcw className="h-5 w-5 text-rose-500" />
                  Recent Refunds
                </CardTitle>
              </CardHeader>
              <CardContent>
                {refunds.length === 0 ? (
                  <p className={`${theme.textMuted} text-center py-8`}>No refunds processed</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={`text-left text-sm ${theme.tableHeader} border-b ${theme.tableRow}`}>
                          <th className="pb-3 font-medium">Refund ID</th>
                          <th className="pb-3 font-medium">Booking ID</th>
                          <th className="pb-3 font-medium">Amount</th>
                          <th className="pb-3 font-medium">Reason</th>
                          <th className="pb-3 font-medium">Date</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {refunds.map((refund) => (
                          <tr key={refund.refund_id} className={`border-b ${theme.tableRow}`}>
                            <td className={`py-3 font-mono text-xs ${theme.tableCell}`}>{refund.refund_id?.slice(-8)}</td>
                            <td className={`py-3 font-mono text-xs ${theme.tableCell}`}>{refund.booking_id?.slice(-8)}</td>
                            <td className="py-3 text-rose-500">{formatCurrency(refund.amount)}</td>
                            <td className={`py-3 ${theme.tableCellMuted}`}>{refund.reason}</td>
                            <td className={`py-3 ${theme.tableCellMuted}`}>{new Date(refund.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPaymentsPage;
